import { Skeleton } from "@workspace/ui/components/skeleton";
import { memo } from "react";

interface CalendarSkeletonProps {
	isHorizontal?: boolean;
}

// Memoize to prevent unnecessary re-renders during loading
export const CalendarSkeleton = memo(function CalendarSkeleton({
	isHorizontal = false,
}: CalendarSkeletonProps) {
	// Shared constants for consistency
	const PLACEHOLDER_COUNT = 3;
	const ROW_HEIGHT = "80px";
	const HEADER_HEIGHT = "h-12";

	// Wrapper classes shared between both layouts
	const wrapperClasses = "w-full overflow-auto rounded-2xl";
	const containerClasses =
		"relative rounded-md border bg-[hsl(var(--calendar-bg))]";
	const cornerCellClasses =
		"sticky top-0 left-0 z-20 h-12 border-r border-b bg-muted";

	if (isHorizontal) {
		return (
			<div className={wrapperClasses}>
				<div className={containerClasses}>
					<div className="grid grid-cols-[96px_1fr]">
						{/* Top-left corner cell */}
						<div className={cornerCellClasses} />

						{/* Time header row */}
						<div className="sticky top-0 z-10 overflow-hidden bg-muted">
							<div
								className={`flex border-b ${HEADER_HEIGHT} items-center px-4 gap-8`}
							>
								{Array.from({ length: PLACEHOLDER_COUNT + 1 }).map((_, i) => (
									<Skeleton key={i} className="h-4 w-12" />
								))}
							</div>
						</div>

						{/* Stage names column */}
						<div className="sticky left-0 z-10 border-r bg-muted/50">
							{Array.from({ length: PLACEHOLDER_COUNT }).map((_, i) => (
								<div
									key={i}
									className="flex items-start border-b px-3 py-3"
									style={{ height: ROW_HEIGHT }}
								>
									<Skeleton className="h-4 w-20" />
								</div>
							))}
						</div>

						{/* Main grid area - empty with subtle animation */}
						<div className="overflow-auto">
							<div
								className="relative animate-pulse bg-gradient-to-r from-transparent via-muted/5 to-transparent"
								style={{
									minWidth: "800px",
									minHeight: `${PLACEHOLDER_COUNT * 80}px`,
								}}
							>
								{/* Empty grid with row dividers */}
								{Array.from({ length: PLACEHOLDER_COUNT }).map((_, i) => (
									<div
										key={i}
										className="border-b"
										style={{ height: ROW_HEIGHT }}
									/>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Vertical layout
	return (
		<div className={wrapperClasses}>
			<div className={containerClasses}>
				<div className="grid grid-cols-[40px_1fr]">
					{/* Top-left corner cell */}
					<div className={cornerCellClasses} />

					{/* Stage headers */}
					<div className="sticky top-0 z-10 overflow-hidden bg-muted">
						<div
							className={`flex ${HEADER_HEIGHT} items-center px-4 gap-8 border-b`}
						>
							{Array.from({ length: PLACEHOLDER_COUNT + 1 }).map((_, i) => (
								<Skeleton key={i} className="h-4 w-24" />
							))}
						</div>
					</div>

					{/* Time labels column */}
					<div className="sticky left-0 z-10 border-r bg-muted/50">
						{Array.from({ length: PLACEHOLDER_COUNT + 1 }).map((_, i) => (
							<div
								key={i}
								className="flex items-start px-2 pt-2"
								style={{ height: "64px" }}
							>
								<Skeleton className="h-3 w-6" />
							</div>
						))}
					</div>

					{/* Main grid area - empty with subtle animation */}
					<div className="overflow-auto">
						<div
							className="animate-pulse bg-gradient-to-b from-transparent via-muted/5 to-transparent"
							style={{
								minWidth: "600px",
								minHeight: "256px",
							}}
						>
							{/* Empty grid with column dividers */}
							<div className="flex h-full">
								{Array.from({ length: PLACEHOLDER_COUNT + 1 }).map((_, i) => (
									<div
										key={i}
										className="border-r flex-1"
										style={{ minHeight: "256px" }}
									/>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
});
